import {inject, injectable} from 'inversify';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import type {WebhookEndpoint} from 'xconvert-web-shared';

import {ResponsePipe} from '../utils/request.utils';
import {ApiConfig} from './api-config';
import {LightHttp} from './light-http.service';
import {LoginService} from './login.service';

/**
 * Portal-side service for managing the signed-in user's
 * WebhookEndpoints + deliveries per developer-api.md §14
 * E2 + E3.
 *
 * Backs the `/portal/webhooks` page: endpoint CRUD + resume
 * + acknowledge-secret + per-delivery redeliver.
 *
 * Matches the same thin-RPC pattern as [ApiKeysService];
 * returns unmodified server payloads so the UI sees the
 * same [WebhookEndpoint] shape that the public-API
 * introspection endpoints emit.
 */
@injectable()
export class WebhooksService {
    constructor(
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(LightHttp) private http: LightHttp,
        @inject(LoginService) private loginService: LoginService,
    ) {}

    // ================================================================
    // Endpoint CRUD
    // ================================================================

    list(includeStopped = false): Observable<WebhookEndpoint[]> {
        this.requireLogin();
        const url = `${this.apiConfig.getWebhookEndpointsEndpoint()}?includeStopped=${includeStopped ? 'true' : 'false'}`;
        return this.http.get<{endpoints: WebhookEndpoint[]}>(url).pipe(
            ResponsePipe,
            map(r => r?.endpoints ?? []),
        );
    }

    get(id: string): Observable<WebhookEndpoint> {
        this.requireLogin();
        return this.http.get<WebhookEndpoint>(
            this.apiConfig.getWebhookEndpointByIdEndpoint(id),
        ).pipe(ResponsePipe);
    }

    create(input: {
        url: string;
        description?: string;
        enabledEvents: string[];
        maxConcurrentDeliveries?: number;
    }): Observable<WebhookEndpointCreateResponse> {
        this.requireLogin();
        this.validateUrl(input.url);
        if (!input.enabledEvents || input.enabledEvents.length === 0) {
            throw new Error('enabledEvents[] must be non-empty');
        }
        return this.http.post<WebhookEndpointCreateResponse>(
            this.apiConfig.getWebhookEndpointsEndpoint(),
            input,
        ).pipe(ResponsePipe);
    }

    update(id: string, patch: Partial<{
        description: string;
        enabledEvents: string[];
        maxConcurrentDeliveries: number;
    }>): Observable<WebhookEndpoint> {
        this.requireLogin();
        return this.http.put<WebhookEndpoint>(
            this.apiConfig.getWebhookEndpointByIdEndpoint(id),
            patch,
        ).pipe(ResponsePipe);
    }

    /**
     * Customer-initiated stop per §5.5.1. Transitions the
     * endpoint from any non-terminal state to
     * `stopped_by_user`. Deliveries pause until [resume] is
     * called.
     */
    stop(id: string): Observable<WebhookEndpoint> {
        this.requireLogin();
        return this.http.delete<WebhookEndpoint>(
            this.apiConfig.getWebhookEndpointByIdEndpoint(id),
        ).pipe(ResponsePipe);
    }

    resume(id: string): Observable<WebhookEndpoint> {
        this.requireLogin();
        return this.http.post<WebhookEndpoint>(
            this.apiConfig.getWebhookEndpointResumeEndpoint(id),
            {},
        ).pipe(ResponsePipe);
    }

    /**
     * Rotate the signing secret per §5.5.4. Returns the new
     * `whsec_...` value exactly ONCE. The previous secret
     * remains valid for 24h per the rotation overlap contract.
     */
    rotateSecret(id: string): Observable<WebhookSecretResponse> {
        this.requireLogin();
        return this.http.post<WebhookSecretResponse>(
            this.apiConfig.getWebhookEndpointRotateSecretEndpoint(id),
            {},
        ).pipe(ResponsePipe);
    }

    /**
     * Clears the `pending_secret_ack` state after an
     * admin-initiated force-rotate. Transitions to `active`
     * (or stays `paused_by_admin` if the endpoint was paused;
     * see §5.5.1 status precedence).
     */
    acknowledgeSecret(id: string): Observable<WebhookEndpoint> {
        this.requireLogin();
        return this.http.post<WebhookEndpoint>(
            this.apiConfig.getWebhookEndpointAckSecretEndpoint(id),
            {},
        ).pipe(ResponsePipe);
    }

    // ================================================================
    // Deliveries (§5.5.5 portal-display)
    // ================================================================

    /**
     * List recent deliveries for the user. `endpointId`
     * filters to a single endpoint's history (portal's
     * "Deliveries" tab inside an endpoint's detail view).
     */
    listDeliveries(endpointId?: string): Observable<WebhookDeliverySummary[]> {
        this.requireLogin();
        return this.http.get<{deliveries: WebhookDeliverySummary[]}>(
            this.apiConfig.getWebhookDeliveriesEndpoint(endpointId),
        ).pipe(
            ResponsePipe,
            map(r => r?.deliveries ?? []),
        );
    }

    /**
     * Manually re-queue a delivery per §5.5.5. Resets the
     * circuit breaker + backoff on the parent endpoint (the
     * customer's "I fixed my side, try again" signal).
     * Rejected (409) when the endpoint is `disabled` or
     * `paused_by_admin`.
     *
     * When `targetEndpointId` is supplied, the delivery is CLONED
     * onto that endpoint instead of replayed on the original — the
     * "Resend to ANY endpoint" affordance per CF-UX-WEBHOOKS-1.
     */
    redeliver(
        deliveryId: string,
        targetEndpointId?: string,
    ): Observable<WebhookDeliverySummary> {
        this.requireLogin();
        const base = this.apiConfig.getWebhookDeliveryRedeliverEndpoint(deliveryId);
        const url = targetEndpointId
            ? `${base}?targetEndpointId=${encodeURIComponent(targetEndpointId)}`
            : base;
        return this.http.post<WebhookDeliverySummary>(url, {}).pipe(ResponsePipe);
    }

    /**
     * Fetch the full detail for one delivery (payload + per-attempt
     * response). Drives the portal's delivery detail drawer.
     * CF-UX-WEBHOOKS-1.
     */
    getDelivery(deliveryId: string): Observable<WebhookDeliveryDetail> {
        this.requireLogin();
        const base = this.apiConfig.getWebhookDeliveriesEndpoint().split('?')[0];
        const url = `${base}/${encodeURIComponent(deliveryId)}`;
        return this.http.get<WebhookDeliveryDetail>(url).pipe(ResponsePipe);
    }

    /**
     * Re-auth-gated secret reveal. The user is already JWT-
     * authenticated, but we make them prove possession of the
     * password before showing the signing secret — same way Stripe
     * gates vault reveals. `password` is the SHA-256-prehashed
     * value (matching the login flow's contract).
     */
    revealSecret(endpointId: string, password: string): Observable<{secret: string}> {
        this.requireLogin();
        const url = `${this.apiConfig.getWebhookEndpointByIdEndpoint(endpointId)}/reveal-secret`;
        return this.http.post<{secret: string}>(url, {password}).pipe(ResponsePipe);
    }

    /**
     * Server-side reachability probe per CF-UX-WEBHOOKS-2-LIVE-PROBE.
     * Browser CORS blocks the form from doing the probe itself, so we
     * delegate to `POST /portal/webhook-endpoints/probe-url`. Returns
     * `{reachable, statusCode, ms, error}` — the chip uses the
     * statusCode to colour `✓ 200` vs `⚠ 401` vs `⛔ timeout`.
     */
    probeUrl(targetUrl: string): Observable<WebhookProbeResponse> {
        this.requireLogin();
        const url = `${this.apiConfig.getWebhookEndpointsEndpoint()}/probe-url`;
        return this.http.post<WebhookProbeResponse>(url, {url: targetUrl}).pipe(ResponsePipe);
    }

    // ================================================================
    // Helpers
    // ================================================================

    private requireLogin() {
        if (!this.loginService.isUserLoggedIn) {
            throw new Error('WebhooksService: user must be logged in');
        }
    }

    /**
     * Client-side URL pre-validation. The server runs the
     * authoritative SSRF check (§5.5.6) — we're just catching
     * obvious typos before the round-trip.
     */
    private validateUrl(url: string): void {
        if (!url || url.trim().length === 0) {
            throw new Error('Webhook endpoint URL is required');
        }
        let parsed: URL;
        try {
            parsed = new URL(url);
        } catch (e) {
            throw new Error(`Webhook endpoint URL is not a valid URL: ${url}`);
        }
        if (parsed.protocol !== 'https:') {
            throw new Error('Webhook endpoint URL must use https:// (§5.5.6)');
        }
    }
}

export interface WebhookEndpointCreateResponse {
    endpoint: WebhookEndpoint;
    /**
     * One-shot signing secret (e.g. `whsec_...`). Returned
     * exactly once at creation time.
     */
    secret: string;
}

export interface WebhookSecretResponse {
    endpoint: WebhookEndpoint;
    /** The rotated secret. */
    secret: string;
}

/**
 * Portal-facing delivery row. Strictly narrower than the
 * full `WebhookDelivery` class from shared-library — omits
 * the internal `claimedBy / claimExpiresAt` fields that
 * customers don't need.
 */
export interface WebhookDeliverySummary {
    id: string;
    eventId: string;
    endpointId: string;
    status: 'PENDING' | 'IN_PROGRESS' | 'DELIVERED' | 'FAILED_TERMINAL';
    attemptNumber: number;
    nextAttemptAt?: string;
    deliveredAt?: string;
    lastError?: string;
    lastHttpStatus?: number;
    lastDurationMs?: number;
    createdAt: string;
}

/**
 * Full detail shape returned by GET /portal/webhook-deliveries/{id}.
 * Includes the signed payload that was POSTed to the receiver and
 * the per-attempt history (response status + body snippet + duration
 * + error). Used by the delivery detail drawer in /portal/webhooks.
 */
export interface WebhookDeliveryDetail extends WebhookDeliverySummary {
    payload?: string | null;
    attempts?: Array<{
        attemptedAt?: string | null;
        httpStatus?: number | null;
        durationMs?: number | null;
        error?: string | null;
        responseBodySnippet?: string | null;
    }>;
}

/**
 * Server-side reachability probe response per CF-UX-WEBHOOKS-2-LIVE-PROBE.
 * `reachable=true` covers any 2xx–4xx response (the receiver answered).
 * `statusCode=null` means the request never connected (timeout / DNS /
 * refused) — the `error` field carries which.
 */
export interface WebhookProbeResponse {
    reachable: boolean;
    statusCode: number | null;
    ms: number;
    error: string | null;
}
